import { useMemo } from 'react';
import { GENDERS, PERSONALITY_PRESETS, type Gender, type PersonalityPreset } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { useTranslation } from './i18n';

/**
 * The persona presets with their NAMES in the app's language.
 *
 * `PERSONALITY_PRESETS` is shared with the server, so `nameByGender` is English on every account ("The
 * Maestro" / "The Maestra") — the same literals the character doc is built from. That reads wrong in a
 * language with no article: a Japanese account's Personality screen and chrome said "The Maestro". So a
 * locale may give the name its own form under `personality.preset.<key>.name.<gender>`, and the shared
 * English value stands wherever it doesn't — which is every locale but `ja` today, and every preset but
 * the Maestro.
 *
 * This localizes the PRESET rather than each read of it, so every rule in `personality-editor.ts`
 * (`seedName` / `defaultName` / `nameToPersist` / `nameOnGenderChange`) keeps working unchanged and stays
 * the 1:1 mirror of the native helpers. In particular `nameToPersist` still compares against the name the
 * field was seeded with, so a Japanese user who never touches it persists nothing and keeps following the
 * preset. Native reaches the same contract from its own side, through `AssistantName.presetName`.
 *
 * The gallery's label/tagline/description are localized separately, by `use-preset-text.ts`.
 */
export function localizePresetNames(
  presets: readonly PersonalityPreset[],
  translate: (key: string, fallback: string) => string,
): PersonalityPreset[] {
  return presets.map((preset) => {
    const nameByGender = { ...preset.nameByGender } as Record<Gender, string>;
    for (const gender of GENDERS) {
      const shared = preset.nameByGender[gender];
      nameByGender[gender] = translate(`personality.preset.${preset.key}.name.${gender}`, shared) || shared;
    }
    return { ...preset, nameByGender };
  });
}

/** The shared preset gallery, named in the active language. Memoized so it is a stable list across
 *  renders (callers put it in effect/callback deps) and re-derives when the language changes. */
export function useLocalizedPresets(): PersonalityPreset[] {
  const { t } = useTranslation();
  return useMemo(() => localizePresetNames(PERSONALITY_PRESETS, (key, fallback) => t(key, { defaultValue: fallback })), [t]);
}
